/**
 * The wordmark's timeline, as pure maths.
 *
 * Everything here is a function of elapsed milliseconds, so the renderer can be a single
 * requestAnimationFrame loop that asks "what should letter i look like at t?" and writes the
 * answer. Two things fall out of that:
 *
 *  - A frame that never arrives costs nothing. A hidden tab gets no animation frames at all, and
 *    the old wordmark -- a Fader per letter on `setInterval(..., 2)`, which Chrome clamps to ~1s in
 *    the background -- would sit half-drawn for minutes before finishing. Time-driven state just
 *    jumps to where it should be on the first frame after the tab comes back.
 *  - The choreography is testable without a renderer, which is the only way to assert the shape of
 *    an animation (does it overshoot? is the stagger right? does it land?) at all.
 */

/** Gap between one letter starting its entrance and the next. */
export const LETTER_STAGGER = 115
/** How long a single letter takes to rise, settle and go white. */
export const LETTER_ENTER_MS = 460
/** How far below its resting place a letter starts, in logical pixels. */
export const RISE = 28
/** A letter enters slightly small and grows into place. */
export const START_SCALE = 0.88
/** Past-the-target spring on the rise; ~15% of RISE, so about 4px of overshoot at 88px type. */
export const OVERSHOOT = 2.2

/** The accent rule under the wordmark starts this long after the last letter begins. */
export const RULE_DELAY = 260
export const RULE_MS = 520

/** When the screen is told the wordmark has arrived (slide-up, background, login card). */
export const SETTLE_DELAY = 300

/** Resting halo behind each letter, and the slow shimmer that rides on top of it. */
export const GLOW_REST = 0.14
export const GLOW_PEAK = 0.62
export const GLOW_BREATH = 0.07
export const BREATH_MS = 3200
/** Offset between neighbouring letters' breathing, so the shimmer travels instead of throbbing. */
export const BREATH_STAGGER = 170

/** A one-off flare, used to tie the glow to the block's slide into place. */
export const PULSE_MS = 700
export const PULSE_PEAK = 0.22

export const clamp01 = (t: number): number => (t < 0 ? 0 : t > 1 ? 1 : t)

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - clamp01(t), 3)

export const easeOutExpo = (t: number): number => (clamp01(t) >= 1 ? 1 : 1 - Math.pow(2, -10 * clamp01(t)))

/** Decelerates past the target and settles back -- the spring in the letters' rise. */
export const easeOutBack = (t: number, overshoot: number = OVERSHOOT): number => {
    const u = clamp01(t) - 1
    return 1 + (overshoot + 1) * u * u * u + overshoot * u * u
}

/** Smoothstep. Holds the ends longer than a linear ramp, which keeps the green in the tint ramp. */
export const smoothstep = (t: number): number => {
    const c = clamp01(t)
    return c * c * (3 - 2 * c)
}

/** Channel-wise blend of two 0xRRGGBB values. */
export const lerpColor = (from: number, to: number, t: number): number => {
    const k = clamp01(t)
    const fr = (from >> 16) & 0xff
    const fg = (from >> 8) & 0xff
    const fb = from & 0xff
    const tr = (to >> 16) & 0xff
    const tg = (to >> 8) & 0xff
    const tb = to & 0xff
    const r = Math.round(fr + (tr - fr) * k)
    const g = Math.round(fg + (tg - fg) * k)
    const b = Math.round(fb + (tb - fb) * k)
    return (r << 16) | (g << 8) | b
}

/** Three-stop ramp: the letter ignites accent, brightens, then cools to the page's white. */
export const rampColor = (cold: number, hot: number, settled: number, t: number): number => {
    const k = smoothstep(t)
    return k < 0.5 ? lerpColor(cold, hot, k * 2) : lerpColor(hot, settled, (k - 0.5) * 2)
}

export interface LetterState {
    /** Offset from the resting baseline; positive is below, negative is the overshoot above it. */
    y: number
    alpha: number
    scale: number
    /** 0..1 along the cold -> hot -> settled ramp; the caller supplies the colours. */
    tintProgress: number
    glowAlpha: number
}

/** Progress of one letter's entrance, 0 before it starts and 1 once it has landed. */
export const letterProgressAt = (index: number, elapsedMs: number): number =>
    clamp01((elapsedMs - index * LETTER_STAGGER) / LETTER_ENTER_MS)

/**
 * A single letter at time t. `pulseElapsed` is the time since the last `pulse()` (or undefined),
 * which adds a brief flare over the resting glow.
 */
export const letterStateAt = (index: number, elapsedMs: number, pulseElapsed?: number): LetterState => {
    const t = letterProgressAt(index, elapsedMs)
    const eased = easeOutBack(t)

    // Peaks mid-entrance and is gone by the time the letter lands, leaving only the resting halo:
    // this is the "ignition" that travels across the word on the stagger.
    const flare = Math.pow(Math.sin(Math.PI * t), 1.5)

    const breath = 0.5 + 0.5 * Math.sin((2 * Math.PI * (elapsedMs - index * BREATH_STAGGER)) / BREATH_MS)
    const pulse =
        pulseElapsed === undefined ? 0 : PULSE_PEAK * Math.sin(Math.PI * clamp01(pulseElapsed / PULSE_MS))

    return {
        y: RISE * (1 - eased),
        alpha: easeOutCubic(t * 1.8),
        scale: START_SCALE + (1 - START_SCALE) * eased,
        tintProgress: t,
        glowAlpha: t * (GLOW_REST + GLOW_BREATH * breath + pulse) + (GLOW_PEAK - GLOW_REST) * flare,
    }
}

/** When the caller is told the wordmark has arrived. */
export const settleTs = (letterCount: number): number => (letterCount - 1) * LETTER_STAGGER + SETTLE_DELAY

export const ruleProgressAt = (elapsedMs: number, letterCount: number): number =>
    clamp01((elapsedMs - ((letterCount - 1) * LETTER_STAGGER + RULE_DELAY)) / RULE_MS)

/** The moment nothing is moving any more except the resting shimmer. */
export const entranceEndTs = (letterCount: number): number =>
    (letterCount - 1) * LETTER_STAGGER + Math.max(LETTER_ENTER_MS, RULE_DELAY + RULE_MS)

// ---- the hand-off flight ----------------------------------------------------------------------
//
// When the HTML loading screen is still up, the wordmark does not rise in from below: each letter
// of the loading screen's small "samsara" flies to its place in the big one. These are written in
// choreography milliseconds -- see intro_pace.ts for the dial that sets the real speed.

/** The letters hold where the loading screen drew them while "Please wait" fades out. */
export const FLIGHT_DELAY = 300
export const FLIGHT_STAGGER = 40
export const FLIGHT_MS = 1150
/** How long after the last letter lands the screen is told the wordmark has arrived. */
export const FLIGHT_SETTLE_GAP = 160
/** The halo the letters start with: the loading screen's text-shadow is a faint 30px blur, and a
 *  stronger copy-halo would make the pixi letters look bolder than the HTML ones they replace. */
export const LOADING_GLOW = 0.05
/** The extra glow a letter picks up at the top of its flight. */
export const FLIGHT_FLARE = 0.48
/** A small swell as each letter lands. */
export const LANDING_BUMP = 0.025
export const LANDING_BUMP_MS = 280
/** The hairline starts drawing a little before the last letter lands. */
export const FLIGHT_RULE_LEAD = 190

export const easeInOutCubic = (t: number): number => {
    const c = clamp01(t)
    return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2
}

export const flightLandTs = (index: number): number => FLIGHT_DELAY + index * FLIGHT_STAGGER + FLIGHT_MS

/** Progress of one letter's flight, 0 while it still sits on the loading screen, 1 once landed. */
export const flightProgressAt = (index: number, elapsedMs: number): number =>
    clamp01((elapsedMs - FLIGHT_DELAY - index * FLIGHT_STAGGER) / FLIGHT_MS)

export interface FlightState {
    /** 0..1 from the loading pose to the resting pose, eased in and out. */
    travel: number
    /** Multiplier on the letter's scale: 1, except for the swell as it lands. */
    bump: number
    /** 0..1 along the cream -> gold -> settled ramp. */
    tintProgress: number
    glowAlpha: number
    /** 0..1 of the resting drop shadow; the loading screen's letters have none. */
    shadow: number
}

export const flightStateAt = (index: number, elapsedMs: number, pulseElapsed?: number): FlightState => {
    const m = flightProgressAt(index, elapsedMs)
    const landed = elapsedMs - flightLandTs(index)
    const bump = landed > 0 ? 1 + LANDING_BUMP * Math.sin(Math.PI * clamp01(landed / LANDING_BUMP_MS)) : 1
    const flare = Math.pow(Math.sin(Math.PI * m), 1.5)
    const breath = 0.5 + 0.5 * Math.sin((2 * Math.PI * (elapsedMs - index * BREATH_STAGGER)) / BREATH_MS)
    const pulse =
        pulseElapsed === undefined ? 0 : PULSE_PEAK * Math.sin(Math.PI * clamp01(pulseElapsed / PULSE_MS))
    const rest = GLOW_REST + GLOW_BREATH * breath + pulse
    const settled = smoothstep(m)

    return {
        travel: easeInOutCubic(m),
        bump,
        tintProgress: m,
        glowAlpha: LOADING_GLOW + (rest - LOADING_GLOW) * settled + FLIGHT_FLARE * flare,
        shadow: settled,
    }
}

/** When the screen is told the wordmark has arrived (tagline, login card). */
export const flightSettleTs = (letterCount: number): number => flightLandTs(letterCount - 1) + FLIGHT_SETTLE_GAP

export const flightRuleProgressAt = (elapsedMs: number, letterCount: number): number =>
    clamp01((elapsedMs - (flightLandTs(letterCount - 1) - FLIGHT_RULE_LEAD)) / RULE_MS)
